import { isNumeric } from '../../util/helpers';

import { LatLon } from '../../util/geodesy/latlon';
import { parseLatLon } from '../util/parseUtils';
import { LandingSurface, LandingSurfaceType } from './landing-surface.model';
import { PatternAltitude } from './pattern-altitude.model';
import { PilotControlledLighting } from './pilot-controlled-lighting.model';
import { Contact } from './contact.model';
import { AirportComment, CommentType } from './airport-comment.model';
import { AirportActivity } from './airport-activity.model';
import { NoiseAbatement } from './noise-abatement.model';
import { ProcedureType, TerminalProcedure, TerminalProcedurePack } from './terminal-procedure.model';
import { AirportReview } from './airport-review.model';
import { ReviewSummary } from '../../shared/models/review-summary.model';
import { AirportTag } from './airport-tag.model';
import { BusinessSummary } from './business-summary.model';
import { FlyingclubSummary } from './flyingclub-summary.model';
import { AirportNavaid } from './airport-navaid.model';
import { FlightComm } from './flight-comm.model';
import { LocalAttraction } from './local-attraction.model';
import { AirportAlert } from './airport-alert.model';
import { Airspace } from './airspace.model';
import { BusinessAdmin } from '../../business/models/business-admin.model';
import { AfmOther } from './afm-other.model';
import { Webcam } from './webcam.model';
import { NOTAM } from './notam.model';


/**
 * Airport operational status
 */
export enum AirportStatus {
  Operational,
  ClosedIndefinitely,
  ClosedPermanently
}


/**
 * Airport type
 */
export enum AirportType {
  Heliport = 'heliport',
  Ultralight = 'ultralight',
  GliderPort = 'gliderport',
  BalloonPort = 'balloonport',
  Airport = 'airport',
  SeaplaneBase = 'seaport'
}


/**
 * Tower facility types
 */
export enum TowerFacilityType {
  /**
   * Air Traffic Control Tower
   */
  ATCT,

  /**
   * Non-ATC Providing IFR Service
   */
  NON_ATCT,

  /**
   * ATCT plus Approach Control
   */
  ATCT_AC,

  /**
   * ATCT (Air Force) plus Radar Approach Control (Air Force operates ATCT, FAA operates approach control).
   */
  ATCT_RAPCON,

  /**
   * ATCT (Navy) plus Radar Approach Control (Navy operates ATCT, FAA operates approach control).
   */
  ATCT_RATCF,

  /**
   * ATCT plus Terminal Radar Approach Control
   */
  ATCT_TRACON
}


/**
 * Airport ownership type
 */
export enum AirportOwnershipType {
  Civil,
  Private,
  Army,
  Navy,
  USAF,
  CoastGuard
}


/**
 * Method used to determine airport elevation
 */
export enum ElevationDeterminationMethod {
  Estimated,
  Surveyed
}


/**
 * Regex for parsing out the magnetic variation value and direction from a string.
 */
export const MAG_VARIATION_REGEX: RegExp = /(\d+)([EW])/;


/**
 * Airport model
 */
export class Airport {  /**
 * Numeric identifier that uniquely identifies this airport in the database
 */
airportId: number;


  /**
   * 4-letter ICAO identifier of the airport (if available)
   */
  icaoId: string;

  /**
   * 3- or 4-letter FAA identifier of the airport (if available)
   */
  faaId: string;

  /**
   * Airport operational status
   */
  status: AirportStatus;

  /**
   * Airport Type
   */
  type: AirportType;

  /**
   * Airport name
   */
  name: string;

  /**
   * Abbreviated airport name
   */
  shortName: string;

  /**
   * Effective date of the FAA data (this will be the date of the last data cycle).
   */
  dataEffectiveDate: Date;

  /**
   * Magnetic variation as a string (e.g., '08W')
   */
  magVariation: string;

  /**
   * Magnetic variation as a numeric value (positive if Westerly, negative if Easterly, or null if not reported)
   */
  get magVariationValue(): number {
    const match = MAG_VARIATION_REGEX.exec(this.magVariation || '');
    if (!match) {
      return null;
    }
    const value = parseInt(match[1], 10);
    const sign = match[2] === 'W' ? 1 : -1;
    return sign * value;
  }

  /**
   * Magnetic variation epoch year (year when magnetic variation for this airport was determined, rounded up to the next
   * future 5-year increment).
   */
  magVariationEpochYear: number;

  /**
   * ARTCC identifier
   */
  artcc: string;

  /**
   * ARTCC name
   */
  artccName: string;

  /**
   * Airspace information
   */
  airspace: Airspace;

  /**
   * Airport latitude and longitude
   */
  location: LatLon;

  /**
   * Airport elevation in feet
   */
  elevation: number;

  /**
   * Method used to determine airport elevation (null if unknown).
   */
  elevationMethod: ElevationDeterminationMethod;

  /**
   * Whether the airport has an operating control tower
   */
  towered: boolean;

  /**
   * Tower facility type
   */
  towerFacilityType: TowerFacilityType;

  /**
   * Tower number of hours of daily operation
   */
  towerHrsPerDay: number;

  /**
   * Indication of tower's regularity of operations (e.g., "Everyday, Year-round", "weekdays only", or "weekdays, other
   * hours weekends").
   */
  towerRegularityOfOps: string;

  /**
   * Approach/Departure Control Facility identifier
   */
  masterAirportTower: string;

  /**
   * Identifier of the facility responsible for issuing notices to airmen (NOTAMs) and weather information for the
   * airport.
   */
  notamIdentifier: string;

  /**
   * Airport diagram thumbnail image URL
   */
  thumbNail: string;

  /**
   * Indicates whether a segmented circle airport marker system is present at the airport (null if no data)
   */
  segmentedCircle: boolean;

  /**
   * Indicates whether a segmented circle (if present) is lighted
   */
  segmentedCircleLighted: boolean;

  /**
   * Indicates whether a wind indicator (e.g., wind sock, wind tee, or tetrahedron) exists at the airport (null if no
   * data)
   */
  windIndicator: boolean;

  /**
   * Indicates whether the wind indicator (if present) is lighted
   */
  windIndicatorLighted: boolean;

  /**
   * Indicates whether an operable airport beacon light is present at the airport (null if no data)
   */
  beacon: boolean;

  /**
   * If beacon is present, indicates the type of beacon (e.g., "split-clear-green" for lighted military airport)
   */
  beaconColor: string;

  /**
   * Indicates whether the airport is attended (null if no data)
   */
  attended: boolean;

  /**
   * Indicates availability of pilot-activated lighting (null if no data)
   */
  pilotActLighting: boolean;

  /**
   * Primary phone number for the airport
   */
  mainPhone: string;

  /**
   * Airport website
   */
  website: string;

  /**
   * Airport Email
   */
  email: string;

  /**
   * Identifier of the Flight Service Station (FSS) primary responsible for providing FSS services at this airport.
   */
  fssId: string;

  /**
   * Name of the Flight Service Station (FSS) primary responsible for providing FSS services at this airport.
   */
  fssName: string;

  /**
   * Phone number of the Flight Service Station (FSS) primarily responsible for providing FSS services at this airport.
   */
  fssPhone: string;

  /**
   * Identifier of the Flight Service Station (FSS) that assumes responsibility for the airport during the off-hours of
   * a part-time primary FSS.
   */
  alternateFssId: string;

  /**
   * Phone number of alternate Flight Service Station (FSS).
   */
  alternateFssPhone: string;

  /**
   * Airport ownership type (e.g., publicly vs private owned, or whether it's owned by army, navy, air force, or coast
   * guard). Null if no data available.
   */
  airportOwnership: AirportOwnershipType;

  /**
   * Indicates whether this airport is public use (null if no data).
   */
  publicUse: boolean;

  /**
   * Indicates whether this airport has been designated by the U.S. Treasury Department as an International Airport of
   * Entry (AOE) for customs (null if data is not available).
   */
  customsIntlEntry: boolean;

  /**
   * Indicates whether this airport has been designated by the U.S. Treasury Department as a customs landing rights
   * airport (null if data is not available).
   */
  customsLandingRights: boolean;

  /**
   * Indicates whether airport has military/civil joint use agreement that allows civil operations at a military airport
   * or military operations at a civil airport (null if data is not available).
   */
  militaryJoint: boolean;

  /**
   * Indicates whether airport has entered into an agreement that grants landing rights to the military.
   */
  militaryLandingRights: boolean;

  /**
   * Address 1 of the airport
   */
  address1: string;

  /**
   * Address 2 of the airport
   */
  address2: string;

  /**
   * Zipcode of the airport
   */
  postalCode: string;

  /**
   * Associated city being served by this airport (note: not necessarily the same city where the airport is physically
   * located).
   */
  city: string;

  /**
   * State or province abbreviation of city being served by this airport (note: not necessarily the same state where the
   * airport is physically located).
   */
  stateProvince: string;

  /**
   * Associated country.
   */
  country: string;

  /**
   * Distance from central business district of the associated city to the airport (rounded to nearest nautical mile).
   */
  distanceFromCity: number;

  /**
   * Direction of airport from central business district of associated city (nearest 1/8 compass point - ex. "NE").
   */
  directionFromCity: string;

  /**
   * Sectional chart where this airport appears (null if data is not available).
   */
  chart: string;

  /**
   * Landing surfaces (runways, helipads, waterways) at the airport
   */
  landingSurfaces: LandingSurface[];

  /**
   * Pattern altitude data (if reported as being non-standard by airport manager or another source)
   */
  patternAltitudes: PatternAltitude[];

  /**
   * Flight communication channels at this airport
   */
  flightComms: FlightComm[];

  /**
   * Pilot-controlled lighting data for this airport (if available)
   */
  pilotControlledLighting: PilotControlledLighting[];

  /**
   * Contacts (e.g., names/phones/addresses of airport manager, owner, etc.)
   */
  contacts: Contact[];

  /**
   * Airport remarks or comments (includes both FAA remarks as well as airport manager comments).
   */
  airportComments: AirportComment[];

  /**
   * Airport activity data (includes number of aircraft based at airport, as well as aircraft operations split out
   * by type).
   */
  activity: AirportActivity;

  /**
   * Noise abatement procedures in effect at this airport.
   */
  noiseAbatement: NoiseAbatement[];

  /**
   * Instrument procedures (and other documents) available for this airport. This is represented as a terminal
   * procedure "pack", which allows access to the current and next data cycles.
   */
  terminalProcedures: TerminalProcedurePack;

  /**
   * Airport reviews and ratings
   */
  reviews: AirportReview[];

  /**
   * Summary object of reviews for this entity
   */
  reviewSummary: ReviewSummary;

  /**
   * List of airport tags that apply to this airport. Airport tags are used to store additional data such as services
   * and fuel availability.
   */
  airportTags: AirportTag[];

  /**
   * Businesses at the airport
   */
  businesses: BusinessSummary[];

  /**
   * Flying clubs at the airport
   */
  flyingClubs: FlyingclubSummary[];

  /**
   * Navaids near airport
   */
  navaids: AirportNavaid[];

  /**
   * Attractions local to the airport
   */
  localAttractions: LocalAttraction[];

  /**
   * Business admins
   */
  admins: Array<BusinessAdmin>;

  /**
   * TimeZone
   */
  timeZone: string;

  /**
   * Source Id
   */
  sourceId: number;

  /**
   * Current FTE Award Year
   */
  fteAwardYear: string;

  /**
   * Gets the identifier that should be displayed for this airport. This will either be the ICAO identifier if
   * available, or the FAA identifier otherwise.
   */
  get identifier(): string {
    return this.icaoId || this.faaId || '';
  }

  /**
   * Returns the airport diagram for the airport if one is available, or null otherwise
   * @returns {TerminalProcedure|null}
   */
  get diagram(): TerminalProcedure {
    return (this.terminalProcedures.current.procedures || [])
      .find((p: TerminalProcedure) => p.type === ProcedureType.AirportDiagram) || null;
  }

  /**
   * Returns true if the airport has a thumbnail diagram available. This is based on there being at least one
   * approach procedure for the airport, since that is what is used to generate the thumbnails. Note that the thumbNail
   * property will always be populated with a URL (since that is how it is returned from the API), but that link is
   * not guaranteed to be valid unless this property is true.
   * @returns {boolean}
   */
  get hasThumbNail(): boolean {
    return (this.terminalProcedures.current.procedures || []).some(p => p.type === ProcedureType.IAP);
  }

  /**
   * Alerts for airport
   */
  alerts: AirportAlert[];

  /**
   * AFM (Airport Facilities Manager) Other data
   */
  afmOther: AfmOther;

  /**
   * Airport webcams
   */
  webcams: Webcam[];

  businessFees: string[];

  pilotAcctLightingFreq: string;

  bodyOfWater: string;

  geoState: string;

  islandProvince: string;

  notamsUS: NOTAM[];

  notamsInternational: NOTAM[];

  tfrs: NOTAM[];

  notamCountUS: number;

  notamCountInternational: number;

  /**
   * Parses an airport as returned by the API into an instance of the Airport class
   */
  static parse(data: any): Airport {
    return Object.assign(new Airport(), {
      airportId: data.airportId,
      icaoId: data.icaoId,
      faaId: data.faaId,
      status: this.parseAirportStatus(data.status),
      type: this.parseAirportType(data.type),
      name: data.name,
      shortName: data.shortName,
      dataEffectiveDate: data.faaData ? (data.faaData.effectiveDate ? new Date(data.faaData.effectiveDate) : null) : null,
      magVariation: data.magVariation || null,
      magVariationEpochYear: data.magVariationEpochYear,
      artcc: data.artcc,
      artccName: data.artccName,
      airspace: data.airspace || {},
      location: data.location ? parseLatLon(data.location.latitude, data.location.longitude) : null,
      elevation: isNumeric(data.elev) ? +data.elev : null,
      elevationMethod: this.parseElevationMethod(data.elevMethod),
      towered: data.towerPresent,
      towerFacilityType: this.parseTowerFacilityType(data.towerFacilityType),
      towerHrsPerDay: isNumeric(data.towerHrsPerDay) ? +data.towerHrsPerDay : null,
      towerRegularityOfOps: data.towerRegularityOfOps || null,
      masterAirportTower: data.masterAirportTower || null,
      notamIdentifier: data.faaData ? data.faaData.notamIdentifier : null,
      thumbNail: data.thumbNail,
      segmentedCircle: data.segmentedCircle,
      segmentedCircleLighted: data.segmentedCircleLighted,
      windIndicator: data.windIndicator,
      windIndicatorLighted: data.windIndicatorLighted,
      beacon: data.beacon,
      beaconColor: data.beaconColor || null,
      attended: data.attended,
      pilotActLighting: data.pilotActLighting,
      mainPhone: data.mainPhone || null,
      email: data.email || null,
      website: data.website || null,
      fssId: data.fssId || null,
      fssPhone: data.fssPhone || null,
      fssName: data.fssName || null,
      alternateFssId: data.alternateFssId || null,
      alternateFssPhone: data.alternateFssPhone || null,
      airportOwnership: this.parseAirportOwnershipType(data.airportOwnership),
      publicUse: this.parsePublicUse(data.airportUsage),
      customsIntlEntry: data.customsIntlEntry,
      customsLandingRights: data.customsLandingRights,
      militaryJoint: data.militaryJoint,
      militaryLandingRights: data.militaryLandingRights,
      address1: data.address1 || null,
      address2: data.address2 || null,
      postalCode: data.zipcode || null,
      city: data.city || null,
      stateProvince: data.stateProvince || null,
      country: data.country || null,
      distanceFromCity: isNumeric(data.distanceFromCity) ? +data.distanceFromCity : null,
      directionFromCity: data.directionFromCity,
      chart: this.parseChart(data.airportCharts),
      landingSurfaces: (data.landingSurfaces || []).map(d => LandingSurface.parse(d)),
      patternAltitudes: (data.patternAltitudes || []).map(d => PatternAltitude.parse(d)),
      flightComms: this.parseFlightComms(data.flightComms),
      pilotControlledLighting: (data.pilotControlledLightingClicks || []).map(d => PilotControlledLighting.parse(d)),
      contacts: (data.contacts || []).map(d => Contact.parse(d)),
      airportComments: (data.airportComments || []).map(d => AirportComment.parse(d)),
      activity: data.faaData ? this.parseActivity(data.faaData) : null,
      noiseAbatement: (data.afmNoises || []).map(d => NoiseAbatement.parse(d)),
      terminalProcedures: TerminalProcedurePack.parse(data.terminalProcedures, data.terminalProceduresNext),
      reviews: (data.airportReviews || []).map(d => AirportReview.parse(d)),
      reviewSummary: data.reviewSummary,
      airportTags: (data.airportTags || []).map(d => AirportTag.parse(d)),
      businesses: (data.businesses || []).map(d => BusinessSummary.parse(d)),
      flyingClubs: (data.flyingClubs || []).map(d => FlyingclubSummary.parse(d)),
      localAttractions: (data.afmTravels || []).map(d => LocalAttraction.parse(d)),
      navaids: (data.navaidsNearAirport || []).map(d => AirportNavaid.parse(d)),
      alerts: (data.alerts || []).map(d => AirportAlert.parse(d)),
      admins: (data.airportAdmins || []).map(d => BusinessAdmin.parse(d)),
      afmOther: data.afmOther ? AfmOther.parse(data.afmOther) : null,
      webcams: (data.webcams || []).map(d => Webcam.parse(d)),
      businessFees: data.businessFees || null,
      timeZone: data.timeZone,
      sourceId: data.sourceId,
      pilotAcctLightingFreq: data.pilotAcctLightingFreq,
      bodyOfWater: data.bodyOfWater,
      geoState: data.geoState,
      islandProvince: data.islandProvince,
      fteAwardYear: data.fteAwardYear,
      notamsUS: (data.notaMsUS || []).map(d => NOTAM.parse(d)),
      notamsInternational: (data.notaMsInternational || []).map(d => NOTAM.parse(d)),
      tfrs: (data.tfRs || []).map(d => NOTAM.parse(d)),
      notamCountUS: data.notamCountUS,
      notamCountInternational: data.notamCountInternational
    });
  }

  /**
   * Parses airport operation status from string
   * @param status {string} Airport status as returned by API
   * @return {AirportStatus} Parsed airport status
   */
  static parseAirportStatus(status: string): AirportStatus {
    switch (status) {
      case 'operational':
        return AirportStatus.Operational;
      case 'closed indefinitely':
        return AirportStatus.ClosedIndefinitely;
      case 'closed permanently':
        return AirportStatus.ClosedPermanently;
      default:
        return null;
    }
  }

  /**
   * Formats airport operation status for display
   * @param status {AirportStatus} Airport status
   * @return {string} Formatted airport status
   */
  static formatAirportStatus(status: AirportStatus): string {
    switch (status) {
      case AirportStatus.Operational:
        return 'Operational';
      case AirportStatus.ClosedIndefinitely:
        return 'Closed Indefinitely';
      case AirportStatus.ClosedPermanently:
        return 'Closed Permanently';
      default:
        return null;
    }
  }

  /**
   * Parses airport type from string
   * @param type {string} Airport type as returned by API
   * @return {AirportType} Parsed airport type
   */
  static parseAirportType(type: string): AirportType {
    switch (type) {
      case 'heliport':
        return AirportType.Heliport;
      case 'ultralight':
        return AirportType.Ultralight;
      case 'gliderport':
        return AirportType.GliderPort;
      case 'balloonport':
        return AirportType.BalloonPort;
      case 'airport':
        return AirportType.Airport;
      case 'seaplane base':
        return AirportType.SeaplaneBase;
      default:
        return null;
    }
  }

  /**
   * Parses tower facility type from string
   * @param type {string} Tower facility type as returned by API
   * @return {TowerFacilityType} Parsed tower facility type
   */
  static parseTowerFacilityType(type: string): TowerFacilityType {
    switch (type) {
      case 'ATCT(Navy) plus Radar Approach Control':
        return TowerFacilityType.ATCT_RATCF;
      case 'ATCT(Air Force) plus Radar Approach Control':
        return TowerFacilityType.ATCT_RAPCON;
      case 'ATCT plus Terminal Radar Approach Control':
        return TowerFacilityType.ATCT_TRACON;
      case 'Non-ATC Providing IFR Service':
        return TowerFacilityType.NON_ATCT;
      case 'ATCT plus Approach Control':
        return TowerFacilityType.ATCT_AC;
      case 'Air Traffic Control Tower':
        return TowerFacilityType.ATCT;
      default:
        return null;
    }
  }

  /**
   * Formats tower facility type for display
   * @param value {TowerFacilityType} Tower facility type
   * @return {string} Formatted tower facility type
   */
  static formatTowerFacilityType(value: TowerFacilityType): string {
    switch (value) {
      case TowerFacilityType.ATCT_RATCF:
        return 'ATCT (Navy) plus Radar Approach Control';
      case TowerFacilityType.ATCT_RAPCON:
        return 'ATCT (Air Force) plus Radar Approach Control';
      case TowerFacilityType.ATCT_TRACON:
        return 'ATCT plus Terminal Radar Approach Control';
      case TowerFacilityType.NON_ATCT:
        return 'Non-ATC Providing IFR Service';
      case TowerFacilityType.ATCT_AC:
        return 'ATCT plus Approach Control';
      case TowerFacilityType.ATCT:
        return 'Air Traffic Control Tower';
      default:
        return null;
    }
  }

  /**
   * Parses airport ownership type from string
   * @param type {string} Airport ownership type as returned by API
   * @return {AirportOwnershipType} Parsed airport ownership type
   */
  static parseAirportOwnershipType(type: string): AirportOwnershipType {
    switch (type) {
      case 'civil':
        return AirportOwnershipType.Civil;
      case 'private':
        return AirportOwnershipType.Private;
      case 'army':
        return AirportOwnershipType.Army;
      case 'navy':
        return AirportOwnershipType.Navy;
      case 'usaf':
        return AirportOwnershipType.USAF;
      case 'coast guard':
        return AirportOwnershipType.CoastGuard;
      default:
        return null;
    }
  }

  /**
   * Formats airport ownership type for display
   * @param type {AirportOwnershipType} Airport ownership type
   * @return {string} Formatted airport ownership type
   */
  static formatAirportOwnershipType(type: AirportOwnershipType): string {
    switch (type) {
      case AirportOwnershipType.Civil:
        return 'Civil';
      case AirportOwnershipType.Private:
        return 'Private';
      case AirportOwnershipType.Army:
        return 'Army';
      case AirportOwnershipType.Navy:
        return 'Navy';
      case AirportOwnershipType.USAF:
        return 'USAF';
      case AirportOwnershipType.CoastGuard:
        return 'Coast Guard';
      default:
        return null;
    }
  }

  /**
   * Parses airport usage type from string
   * @param type {string} Airport usage type as returned by API
   * @return {boolean} Parsed airport usage type
   */
  static parsePublicUse(type: string): boolean {
    switch (type) {
      case 'public':
        return true;
      case 'private':
        return false;
      default:
        return null;
    }
  }

  /**
   * Parses elevation determination method from string
   * @param type {string} Elevation determination method as returned by API
   * @return {ElevationDeterminationMethod} Parsed elevation determination method
   */
  static parseElevationMethod(type: string): ElevationDeterminationMethod {
    switch (type) {
      case 'Estimated':
        return ElevationDeterminationMethod.Estimated;
      case 'Surveyed':
        return ElevationDeterminationMethod.Surveyed;
      default:
        return null;
    }
  }

  /**
   * Formats elevation determination method for display
   * @param value {ElevationDeterminationMethod} Elevation determination method
   * @return {string} Formatted elevation determination method
   */
  static formatElevationMethod(value: ElevationDeterminationMethod): string {
    switch (value) {
      case ElevationDeterminationMethod.Estimated:
        return 'Estimated';
      case ElevationDeterminationMethod.Surveyed:
        return 'Surveyed';
      default:
        return null;
    }
  }

  /**
   * Parses which sectional chart the airport appears in (if any)
   * @param data {any[]} Airport chart data as returned by API
   * @return {string} Chart publisher, name, and type
   */
  static parseChart(data: any[]): string {
    if (!data || data.length < 1) {
      return null;
    }
    // Assume only a single chart per airport (API data model implies there may be more than one, but it is never the
    // case since it's a one-to-one relationship in the source FAA data).
    const chart = data[0].chart;
    if (chart) {
      const {
        publisher,
        chartName,
        chartType
      } = chart;
      return `${publisher}: ${chartName} ${chartType}`;
    }
    return null;
  }

  /**
   * Parses flight communication channels at airport
   * @param data {any[]} Flight comm data as returned by API
   * @return {FlightComm[]} Parsed flight comms
   */
  static parseFlightComms(data: any[]): FlightComm[] {
    if (!data) {
      return [];
    }
    // For each flight comm, the API currently returns a list of one or more frequencies. There are only a handful of
    // cases where a single communication "channel" (e.g., the ASOS weather station) is associated with multiple
    // frequencies, and it's conceptually simpler to display them as two separate communication channels anyway. So
    // any flight comm channel that has multiple frequencies will be returned as a separate FlightComm instance.
    const result: FlightComm[] = [];
    for (const item of data) {
      for (const freq of item.frequencies) {
        result.push(FlightComm.parse({
          flightCommId: item.flightCommId,
          airportId: item.airportId,
          service: item.service,
          radioCall: item.radioCall,
          shortName: item.shortName,
          frequencyId: freq.frequencyId,
          freqMode: freq.freqMode,
          frequency: freq.frequency,
          sector: freq.sector,
          phoneNumber: freq.phoneNumber,
          phoneType: freq.phoneType
        }));
      }
    }
    return result;
  }

  /**
   * Parses aircraft activity data for airport
   * @param data {any} FAA data as returned by API
   * @return {AirportActivity} Airport activity data
   */
  static parseActivity(data: any): AirportActivity {
    return {
      activityEndingDate: data.activityEndingDate ? new Date(data.activityEndingDate) : null,
      gaAircraftSingleEngine: data.gaAircraftSingleEngine,
      gaAircraftMultiEngine: data.gaAircraftMultiEngine,
      gaAircraftJet: data.gaAircraftJet,
      gaAircraftHelo: data.gaAircraftHelo,
      gaAircraftGliders: data.gaAircraftGliders,
      gaAircraftUltralights: data.gaAircraftUltralights,
      activityAirTaxi: data.activityAirTaxi,
      activityGaLocal: data.activityGaLocal,
      activityGaItinerant: data.activityGaItinerant,
      activityMil: data.activityMil,
      activityCommercial: data.activityCommercial,
      activityCommuter: data.activityCommuter
    };
  }

  /**
   * Returns a boolean indicating whether an airport has the passed in landing surface type or not
   * @param type, Type {LandingSurfaceType}
   */
  public hasLandingSurfaceType(type: LandingSurfaceType): boolean {
    return this.landingSurfaces.some(sfc => sfc.type === type);
  }

  /**
   * Helper method to return airport comments of the specified type.
   * @param {CommentType} type
   * @returns {Array<AirportComment>}
   */
  public getComments(type: CommentType): Array<AirportComment> {
    return this.airportComments.filter(c => c.commentType === type);
  }

  public serialize(): any {
    return {
      sourceId: this.sourceId,
      name: this.name,
      shortName: this.shortName,
      location: { latitude: this.location.lat, longitude: this.location.lon },
      icaoId: this.icaoId,
      faaId: this.faaId,
      address1: this.address1,
      address2: this.address2,
      city: this.city,
      stateProvince: this.stateProvince,
      zipcode: this.postalCode,
      country: this.country,
      timeZone: this.timeZone,
      type: this.type,
      status: Airport.formatAirportStatus(this.status),
      website: this.website,
      mainPhone: this.mainPhone,
      masterAirportTower: this.masterAirportTower,
      fssName: this.fssName,
      fssId: this.fssId,
      fssPhone: this.fssPhone,
      magVar: this.magVariation,
      magVariationEpochYear: this.magVariationEpochYear,
      windIndicator: this.windIndicator,
      windIndicatorLighted: this.windIndicatorLighted,
      segmentedCircle: this.segmentedCircle,
      segmentedCircleLighted: this.segmentedCircleLighted,
      beacon: this.beacon,
      beaconColor: this.beaconColor,
      airportUsage: this.publicUse === true ? 'public' : 'private',
      airportOwnership: this.airportOwnership,
      elev: this.elevation,
      towerPresent: this.towered,
      towerFacilityType: this.towerFacilityType,
      towerHrsPerDay: this.towerHrsPerDay,
      towerRegularityOfOps: this.towerRegularityOfOps,
      attended: this.attended,
      pilotActLighting: this.pilotActLighting,
      distanceFromCity: this.distanceFromCity,
      directionFromCity: this.directionFromCity,
      pilotAcctLightingFreq: this.pilotAcctLightingFreq,
      bodyOfWater: this.bodyOfWater,
      geoState: this.geoState,
      islandProvince: this.islandProvince
    };
  }
}
